import type { ArraySchema, MapSchema, SetSchema } from "@colyseus/schema"
import type { Board } from "../core/board"
import type Dps from "../core/dps"
import type { Effect as EffectClass } from "../core/effects/effect"
import type Count from "../models/colyseus-models/count"
import type ExperienceManager from "../models/colyseus-models/experience-manager"
import type { IPokemonRecord } from "../models/colyseus-models/game-record"
import type HistoryItem from "../models/colyseus-models/history-item"
import type Player from "../models/colyseus-models/player"
import type { PlayerChoice } from "../models/colyseus-models/player-choice"
import type { Pokemon } from "../models/colyseus-models/pokemon"
import type { PokemonCustoms } from "../models/colyseus-models/pokemon-customs"
import type Status from "../models/colyseus-models/status"
import type Synergies from "../models/colyseus-models/synergies"
import type { Effects } from "../models/effects"
import type GameRoom from "../rooms/game-room"
import type { AttackSprite } from "./Animation"
import type { EvolutionRule } from "./EvolutionRules"
import type { Ability } from "./enum/Ability"
import type { DungeonPMDO } from "./enum/Dungeon"
import type { BoardEffect, EffectEnum } from "./enum/Effect"
import type { EloRank } from "./enum/EloRank"
import type { Emotion } from "./enum/Emotion"
import type { FlowerPot } from "./enum/FlowerPot"
import type {
  GameMode,
  Orientation,
  PokemonActionState,
  Rarity,
  Stat,
  Team
} from "./enum/Game"
import type { Item } from "./enum/Item"
import type { Passive } from "./enum/Passive"
import type { Pkm } from "./enum/Pokemon"
import type { Synergy } from "./enum/Synergy"
import type { TradeStatus } from "./enum/TradeStatus"
import type { Weather } from "./enum/Weather"
import type { GameStats } from "./interfaces/GameStats"

export * from "./enum/Emotion"
export * from "./enum/FlowerPot"
export * from "./enum/Item"

export const CDN_URL =
  "https://raw.githubusercontent.com/keldaanCommunity/SpriteCollab/master"

// Compatibility helpers kept for the Chinese client modules that still build
// asset paths through the pre-6.11 API. Assets are served from the current
// origin, while portrait fallbacks use the official SpriteCollab endpoint.
export const CDN_PORTRAIT_URL = () => `${CDN_URL}/portrait/`
export const assetUrl = () => ""

export type PkmCustom = { shiny?: boolean; emotion?: Emotion }
export interface PkmWithCustom extends PkmCustom {
  name: Pkm
}

export enum Role {
  ADMIN = "ADMIN",
  MODERATOR = "MODERATOR",
  BASIC = "BASIC",
  BOT = "BOT",
  BOT_MANAGER = "BOT_MANAGER"
}

export enum Transfer {
  DRAG_DROP = "DRAG_DROP",
  DRAG_DROP_COMBINE = "DRAG_DROP_COMBINE",
  DRAG_DROP_ITEM = "DRAG_DROP_ITEM",
  SWITCH_BENCH_AND_BOARD = "SWITCH_BENCH_AND_BOARD",
  SELL_POKEMON = "SELL_POKEMON",
  USE_ITEM = "USE_ITEM",
  REMOVE_FROM_SHOP = "REMOVE_FROM_SHOP",
  NEW_MESSAGE = "NEW_MESSAGE",
  CHANGE_NAME = "CHANGE_NAME",
  CHANGE_AVATAR = "CHANGE_AVATAR",
  ADD_BOT = "ADD_BOT",
  ADD_BOT_BY_TAG = "ADD_BOT_BY_TAG",
  REMOVE_BOT = "REMOVE_BOT",
  TOGGLE_READY = "TOGGLE_READY",
  CHANGE_NO_ELO = "CHANGE_NO_ELO",
  REFRESH = "REFRESH",
  SPECTATE = "SPECTATE",
  LOCK = "LOCK",
  LEVEL_UP = "LEVEL_UP",
  SHOP = "SHOP",
  COOK = "COOK",
  DIG = "DIG",
  GAME_START = "GAME_START",
  GAME_START_REQUEST = "GAME_START_REQUEST",
  GAME_END = "GAME_END",
  CHANGE_ROOM_NAME = "CHANGE_ROOM_NAME",
  CHANGE_ROOM_PASSWORD = "CHANGE_ROOM_PASSWORD",
  CHANGE_ROOM_RANKS = "CHANGE_ROOM_RANKS",
  CHANGE_SPECIAL_RULE = "CHANGE_SPECIAL_RULE",
  BUY_EMOTION = "BUY_EMOTION",
  BOOSTER_CONTENT = "BOOSTER_CONTENT",
  GIFTCARD_RETURN = "GIFTCARD_RETURN",
  USER = "USER",
  DRAG_DROP_CANCEL = "DRAG_DROP_CANCEL",
  SHOW_EMOTE = "SHOW_EMOTE",
  FINAL_RANK = "FINAL_RANK",
  DOUBLE_UP_REINFORCEMENT_SENT = "DOUBLE_UP_REINFORCEMENT_SENT",
  SEARCH_BY_ID = "SEARCH_BY_ID",
  SET_TITLE = "SET_TITLE",
  REMOVE_MESSAGE = "REMOVE_MESSAGE",
  NEW_TOURNAMENT = "NEW_TOURNAMENT",
  DELETE_TOURNAMENT = "DELETE_TOURNAMENT",
  REMAKE_TOURNAMENT_LOBBY = "REMAKE_TOURNAMENT_LOBBY",
  PARTICIPATE_TOURNAMENT = "PARTICIPATE_TOURNAMENT",
  GIVE_BOOSTER = "GIVE_BOOSTER",
  USE_GIFTCARD = "USE_GIFTCARD",
  CREATE_GIFTCARD = "CREATE_GIFTCARD",
  SET_ROLE = "SET_ROLE",
  GIVE_TITLE = "GIVE_TITLE",
  CHOICE = "CHOICE",
  KICK = "KICK",
  DELETE_ROOM = "DELETE_ROOM",
  BAN = "BAN",
  ALERT = "ALERT",
  POKEMON_DAMAGE = "POKEMON_DAMAGE",
  POKEMON_HEAL = "POKEMON_HEAL",
  DISPLAY_TEXT = "DISPLAY_TEXT",
  WANDERER = "WANDERER",
  WANDERER_CLICKED = "WANDERER_CLICKED",
  VECTOR = "VECTOR",
  LOADING_PROGRESS = "LOADING_PROGRESS",
  LOADING_COMPLETE = "LOADING_COMPLETE",
  PLAYER_INCOME = "PLAYER_INCOME",
  PLAYER_DAMAGE = "PLAYER_DAMAGE",
  ROOMS = "ROOMS",
  REQUEST_ROOM = "REQUEST_ROOM",
  ADD_ROOM = "ADD_ROOM",
  REMOVE_ROOM = "REMOVE_ROOM",
  UNBAN = "UNBAN",
  BOARD_EVENT = "BOARD_EVENT",
  CLEAR_BOARD_EVENT = "CLEAR_BOARD_EVENT",
  CLEAR_BOARD = "CLEAR_BOARD",
  SIMULATION_STOP = "SIMULATION_STOP",
  ABILITY = "ABILITY",
  SELECT_LANGUAGE = "SELECT_LANGUAGE",
  SELECT_PAL = "SELECT_PAL",
  USER_PROFILE = "USER_PROFILE",
  PICK_BERRY = "PICK_BERRY",
  PRELOAD_MAPS = "PRELOAD_MAPS",
  NPC_DIALOG = "NPC_DIALOG",
  DELETE_ACCOUNT = "DELETE_ACCOUNT",
  MAINTENANCE = "MAINTENANCE",
  RECONNECT_PROMPT = "RECONNECT_PROMPT",
  OVERWRITE_BOARD = "OVERWRITE_BOARD",
  NOTIFICATIONS = "NOTIFICATIONS",
  NOTIFICATION_SEEN = "NOTIFICATION_SEEN",
  DEBUG_COMMAND = "DEBUG_COMMAND",
  CHANGE_SELECTED_EMOTION = "CHANGE_SELECTED_EMOTION",
  BUY_BOOSTER = "BUY_BOOSTER",
  OPEN_BOOSTER = "OPEN_BOOSTER",
  ITEM = "ITEM",
  POKEMON_PROPOSITION = "POKEMON_PROPOSITION",
  DEV = "DEV",
  CANCEL_TRADE_OFFER = "CANCEL_TRADE_OFFER",
  SELECT_PARTNER = "SELECT_PARTNER",
  TRADE_ACCEPT = "TRADE_ACCEPT"
}

export enum ReadWriteMode {
  WRITE = "WRITE",
  ERASE = "ERASE"
}

export interface ICreditName {
  contact: string
  discord: string
  name: string
}

export interface IChatV2 {
  id: string
  payload: string
  authorId: string
  author: string
  avatar: string
  time: number
}

export interface IClient {
  auth?: {
    uid: string
  }
}

export interface IDragDropMessage {
  x: number
  y: number
  id: string
}

export interface IDragDropItemMessage {
  zone: string
  index: number
  id: Item
}

export interface IDragDropCombineMessage {
  itemA: Item
  itemB: Item
}

export interface ISimplePlayer {
  elo: number
  games: number
  name: string
  id: string
  rank: number
  avatar: string
  title: Title | ""
  role: Role
  pokemons: IPokemonRecord[] | ArraySchema<IPokemonRecord>
  synergies:
    | Array<{ name: Synergy; value: number }>
    | ArraySchema<{ name: Synergy; value: number }>
}

export interface IAfterGamePlayer extends ISimplePlayer {
  gameStats: GameStats
}

export interface IGameHistorySimplePlayer extends ISimplePlayer {
  pokemons: IGameHistoryPokemonRecord[]
}

export interface IGameHistoryPokemonRecord extends IPokemonRecord {
  inventory: Item[]
}

export interface IPokemonAvatar {
  id: string
  name: Pkm
  shiny: boolean
  x: number
  y: number
  action: PokemonActionState
  portalId: string
  itemId: string
}

export interface IFloatingItem {
  id: string
  name: Item
  x: number
  y: number
}

export interface IPortal {
  id: string
  x: number
  y: number
}

export interface ISynergySymbol {
  id: string
  x: number
  y: number
  synergy: Synergy
  portalId: string
}

export interface IPlayer {
  id: string
  name: string
  avatar: string
  board: MapSchema<Pokemon>
  shop: ArraySchema<Pkm>
  simulationId: string
  team: Team
  experienceManager: ExperienceManager
  synergies: Synergies
  money: number
  life: number
  shopLocked: boolean
  shopFreeRolls: number
  streak: number
  interest: number
  doubleUpPartnerId: string
  doubleUpTeamId: string
  tradeStatus: TradeStatus
  opponentId: string
  opponentName: string
  opponentAvatar: string
  opponentTitle: Title | "WILD" | ""
  boardSize: number
  items: ArraySchema<Item>
  scarvesItems: ArraySchema<Item>
  fairyWands: ArraySchema<Item>
  rank: number
  elo: number
  alive: boolean
  history: ArraySchema<HistoryItem>
  pokemonCustoms: PokemonCustoms
  title: Title | ""
  role: Role
  choices: ArraySchema<PlayerChoice>
  loadingProgress: number
  berryTreesStages: number[]
  flowerPots: Pokemon[]
  flowerPotsSpawnOrder: FlowerPot[]
  mulch: number
  mulchCap: number
  effects: Effects
  isBot: boolean
  map: DungeonPMDO | "town"
  regionalPokemons: ArraySchema<Pkm>
  commonRegionalPool: Pkm[]
  uncommonRegionalPool: Pkm[]
  rareRegionalPool: Pkm[]
  epicRegionalPool: Pkm[]
  ultraRegionalPool: Pkm[]
  opponents: Map<string, number>
  ghost: boolean
  eggChance: number
  goldenEggChance: number
  cellBattery: number
  lightX: number
  lightY: number
  titles: Set<Title>
  regions: DungeonPMDO[]
  gameStats: GameStats
  groundHoles: number[]
}

export interface IPokemon {
  id: string
  name: Pkm
  types: SetSchema<Synergy>
  rarity: Rarity
  index: string
  evolution: Pkm
  evolutions: Pkm[]
  evolutionRule: EvolutionRule
  positionX: number
  positionY: number
  speed: number
  def: number
  speDef: number
  atk: number
  hp: number
  maxHP: number
  shield: number
  range: number
  stars: number
  pp: number
  maxPP: number
  luck: number
  ap: number
  critChance: number
  critPower: number
  stacks: number
  stacksRequired: number
  skill: Ability
  tm: Ability
  passive: Passive
  items: SetSchema<Item>
  dishes: SetSchema<Item>
  shiny: boolean
  emotion: Emotion
  additional: boolean
  regional: boolean
  action: PokemonActionState
  canBePlaced: boolean
  canBeCloned: boolean
  canHoldItems: boolean
  canEat: boolean
  deathCount: number
  killCount: number
  readonly hasEvolution: boolean
  supercharged: boolean
}

export interface IExperienceManager {
  level: number
  experience: number
  expNeeded: number
  maxLevel: number
}

export interface ISimulation {
  room: GameRoom
  board: Board
  id: string
  weather: Weather
  bluePlayer: IPlayer | undefined
  redPlayer: IPlayer | undefined
  blueEffects: Set<EffectEnum>
  redEffects: Set<EffectEnum>
  blueTeam: MapSchema<IPokemonEntity>
  redTeam: MapSchema<IPokemonEntity>
  blueDpsMeter: MapSchema<Dps>
  redDpsMeter: MapSchema<Dps>
  bluePlayerId: string
  redPlayerId: string
  broadcastToSpectators(transfer: Transfer, data: any): void
}

export interface ISimulationCommand {
  delay: number
  executed: boolean
  update(dt: number): void
  execute(): void
}

export interface IDps {
  update(
    physicalDamage: number,
    specialDamage: number,
    trueDamage: number,
    physicalDamageReduced: number,
    specialDamageReduced: number,
    shieldDamageTaken: number,
    heal: number,
    shield: number
  )
  id: string
  name: string
  physicalDamage: number
  specialDamage: number
  trueDamage: number
  physicalDamageReduced: number
  specialDamageReduced: number
  shieldDamageTaken: number
  heal: number
  shield: number
}

export interface IPokemonEntity {
  simulation: ISimulation
  refToBoardPokemon: IPokemon
  heldComfey?: IPokemon
  get player(): IPlayer | undefined
  broadcastAbility(options: any): void
  applyStat(stat: Stat, value: number): void
  addAbilityPower(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean,
    permanent?: boolean
  ): void
  addLuck(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean,
    permanent?: boolean
  ): void
  addPP(
    value: number,
    caster: IPokemonEntity,
    apBoost: number,
    crit: boolean
  ): void
  addAttack(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean,
    permanent?: boolean
  ): void
  addSpeed(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean,
    permanent?: boolean
  ): void
  addMaxHP(
    value: number,
    caster: IPokemonEntity,
    apBoost: number,
    crit: boolean,
    permanent?: boolean
  ): void
  addShield(
    value: number,
    caster: IPokemonEntity,
    apBoost: number,
    crit: boolean
  ): void
  addDefense(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean,
    permanent?: boolean
  ): void
  addSpecialDefense(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean,
    permanent?: boolean
  ): void
  addCritChance(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean
  ): void
  addCritPower(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean
  ): void
  addDodgeChance(
    value: number,
    caster: IPokemonEntity | "environment",
    apBoost: number,
    crit: boolean
  ): void
  addItem(item: Item, permanent?: boolean): void
  removeItem(item: Item, permanent?: boolean): void
  update(dt: number, board: Board, player: Player | undefined): void
  skydiveTo(x: number, y: number, board: Board): void
  toIdleState(): void
  toMovingState(): void
  isTargettableBy(
    attacker: IPokemonEntity,
    targetEnemies?: boolean,
    targetAllies?: boolean
  ): boolean
  setTarget(target: IPokemonEntity | null): void
  physicalDamage: number
  specialDamage: number
  trueDamage: number
  physicalDamageReduced: number
  specialDamageReduced: number
  shieldDamageTaken: number
  shieldDone: number
  positionX: number
  positionY: number
  action: PokemonActionState
  index: string
  id: string
  orientation: Orientation
  critChance: number
  maxHP: number
  pp: number
  maxPP: number
  atk: number
  def: number
  speDef: number
  luck: number
  baseTeam: Team
  baseAtk: number
  baseDef: number
  baseSpeDef: number
  hp: number
  shield: number
  team: number
  range: number
  speed: number
  targetX: number
  targetY: number
  targetEntityId: string
  rarity: Rarity
  name: Pkm
  effects: SetSchema<EffectEnum>
  items: SetSchema<Item>
  types: SetSchema<Synergy>
  stars: number
  skill: Ability
  tm: Ability
  passive: Passive
  status: Status
  count: Count
  critPower: number
  ap: number
  healDone: number
  shiny: boolean
  emotion: Emotion
  stacks: number
  stacksRequired: number
  isSpawn: boolean
  commands: ISimulationCommand[]
  effectsSet: Set<EffectClass>
  inSpotlight: boolean
}

export interface IStatus {
  burn: boolean
  silence: boolean
  fatigue: boolean
  poisonStacks: number
  freeze: boolean
  protect: boolean
  sleep: boolean
  confusion: boolean
  wound: boolean
  resurrection: boolean
  resurrecting: boolean
  paralysis: boolean
  pokerus: boolean
  locked: boolean
  possessed: boolean
  blinded: boolean
  armorReduction: boolean
  runeProtect: boolean
  electricField: boolean
  psychicField: boolean
  grassField: boolean
  fairyField: boolean
}

export interface ICount {
  crit: number
  ult: number
  fieldCount: number
  fairyCritCount: number
  attackCount: number
  fightingBlockCount: number
  dodgeCount: number
  starDustCount: number
  tripleAttackCount: number
  staticHolderCount: number
  muscleBandCount: number
  machRibbonCount: number
  spellBlockedCount: number
  manaBurnCount: number
  moneyCount: number
}

export interface IPreparationMetadata {
  name: string
  ownerName: string
  passwordProtected: boolean
  noElo: boolean
  type: "preparation"
  gameStartedAt: string | null
  minRank: EloRank | null
  maxRank: EloRank | null
  gameMode: GameMode
  whitelist: string[]
  blacklist: string[]
  playersInfo: string[]
  tournamentId: string | null
  bracketId: string | null
}

export interface IGameMetadata {
  name: string
  ownerName: string
  gameMode: GameMode
  playerIds: string[]
  playersInfo: string[]
  stageLevel: number
  type: "game"
  tournamentId: string | null
  bracketId: string | null
}

export interface ISuggestionUser {
  name: string
  elo: number
  level: number
  id: string
  avatar: string
  banned?: boolean
}

export enum Title {
  NOVICE = "NOVICE",
  ROOKIE = "ROOKIE",
  AMATEUR = "AMATEUR",
  VETERAN = "VETERAN",
  PRO = "PRO",
  EXPERT = "EXPERT",
  ELITE = "ELITE",
  MASTER = "MASTER",
  GRAND_MASTER = "GRAND_MASTER",
  BIRD_KEEPER = "BIRD_KEEPER",
  BLACK_BELT = "BLACK_BELT",
  BUG_MANIAC = "BUG_MANIAC",
  CUTE_MANIAC = "CUTE_MANIAC",
  DELINQUENT = "DELINQUENT",
  DRAGON_TAMER = "DRAGON_TAMER",
  FIREFIGHTER = "FIREFIGHTER",
  TEAM_ROCKET_GRUNT = "TEAM_ROCKET_GRUNT",
  HIKER = "HIKER",
  LONE_WOLF = "LONE_WOLF",
  KINDLER = "KINDLER",
  GARDENER = "GARDENER",
  MUSEUM_DIRECTOR = "MUSEUM_DIRECTOR",
  ENGINEER = "ENGINEER",
  TELEKINESIST = "TELEKINESIST",
  ELECTRICIAN = "ELECTRICIAN",
  GEOLOGIST = "GEOLOGIST",
  MYTH_TRAINER = "MYTH_TRAINER",
  SURFER = "SURFER",
  POKEMON_RANGER = "POKEMON_RANGER",
  CAMPER = "CAMPER",
  RIVAL = "RIVAL",
  SKIER = "SKIER",
  POKEFAN = "POKEFAN",
  HEX_MANIAC = "HEX_MANIAC",
  MUSICIAN = "MUSICIAN",
  BABYSITTER = "BABYSITTER",
  ALCHEMIST = "ALCHEMIST",
  BERSERKER = "BERSERKER",
  BLOB = "BLOB",
  CHEF = "CHEF",
  HARLEQUIN = "HARLEQUIN",
  TACTICIAN = "TACTICIAN",
  STRATEGIST = "STRATEGIST",
  NURSE = "NURSE",
  GARDIAN = "GARDIAN",
  COLLECTOR = "COLLECTOR",
  DUKE = "DUKE",
  DUCHESS = "DUCHESS",
  CHAMPION = "CHAMPION",
  ELITE_FOUR_MEMBER = "ELITE_FOUR_MEMBER",
  GYM_LEADER = "GYM_LEADER",
  GYM_CHALLENGER = "GYM_CHALLENGER",
  GYM_TRAINER = "GYM_TRAINER",
  ACE_TRAINER = "ACE_TRAINER",
  TYRANT = "TYRANT",
  SURVIVOR = "SURVIVOR",
  GAMBLER = "GAMBLER",
  NATURAL = "NATURAL",
  BOT_BUILDER = "BOT_BUILDER",
  SHINY_SEEKER = "SHINY_SEEKER",
  ARCHEOLOGIST = "ARCHEOLOGIST",
  PRIMAL = "PRIMAL",
  DENTIST = "DENTIST",
  FISHERMAN = "FISHERMAN",
  MOLE = "MOLE",
  BLOSSOMED = "BLOSSOMED",
  SIREN = "SIREN",
  FEARSOME = "FEARSOME",
  GOLDEN = "GOLDEN",
  LUCKY = "LUCKY",
  GIANT = "GIANT",
  DECURION = "DECURION",
  LEGEND = "LEGEND",
  CHOSEN_ONE = "CHOSEN_ONE",
  ANNIHILATOR = "ANNIHILATOR",
  VANQUISHER = "VANQUISHER",
  OUTSIDER = "OUTSIDER",
  GLUTTON = "GLUTTON",
  PICNICKER = "PICNICKER",
  STARGAZER = "STARGAZER",
  BLOODY = "BLOODY",
  ETERNAL = "ETERNAL",
  RUNNER = "RUNNER",
  FINISHER = "FINISHER",
  VICTORIOUS = "VICTORIOUS",
  AQUARIOPHILE = "AQUARIOPHILE",
  POFFIN_MASTER = "POFFIN_MASTER",
  TOP_GUN = "TOP_GUN",
  SCOUT = "SCOUT",
  RESCUE_TEAM_MEMBER = "RESCUE_TEAM_MEMBER",
  EXPLORER = "EXPLORER",
  POSTMAN = "POSTMAN",
  SURVEY_CORPS = "SURVEY_CORPS",
  GUILDMASTER = "GUILDMASTER",
  LEGIONNAIRE = "LEGIONNAIRE",
  FIVE_STARS = "FIVE_STARS",
  PAL = "PAL"
}

export interface IBoardEvent {
  simulationId: string
  effect: BoardEffect
  x: number
  y: number
}

export interface IAttackEvent {
  simulationId: string
  pokemonId: string
  targetX: number
  targetY: number
  travelTime: number
  delay: number
}

export { AttackSprite }
